import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import Select from "react-select";
import { UPDATE_PROJECT_GROUP_MUTATION } from "../../../../graphql/mutations";

const EditProjectGroupModal = ({
  show,
  projectGroup,
  assetTypeId,
  handleClose,
  env,
  tenantName,
  tenantId,
  handleEnvChange,
  viewOnly,
  assetTypes = [],
  countries = [],
  segments = []
}) => {
  const navigate = useNavigate();
  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    operator: "",
    segment: "",
    publicId: "",
    country: "",
    region: "",
    latitude: "",
    longitude: "",
    assetTypeId: "",
    assetTypeName: ""
  });

  useEffect(() => {
    if (projectGroup) {
      const locationParts = (projectGroup.location || "").split(",");
      const country = locationParts[0] ? locationParts[0].trim() : "";
      const region = locationParts[1] ? locationParts[1].trim() : "";

      setFormData({
        name: projectGroup.name || "",
        operator: projectGroup.operator || "",
        segment: projectGroup.segment || "",
        publicId: projectGroup.publicId || "",
        country: country,
        region: region,
        latitude: projectGroup.coordinates ? projectGroup.coordinates[0] : "",
        longitude: projectGroup.coordinates ? projectGroup.coordinates[1] : "",
        assetTypeId: assetTypeId || "",
        assetTypeName: projectGroup.assetType
      });

      setFormErrors({});
    }
  }, [projectGroup, assetTypes, assetTypeId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectChange = (selectedOption, actionMeta) => {
    const name = actionMeta.name;
    const value = selectedOption ? selectedOption.value : "";

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
      // if selecting asset type, update the asset type name too
      ...(name === "assetTypeId" && selectedOption ? { assetTypeName: selectedOption.label } : {})
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }

    // if segment changes, clear asset type
    if (name === "segment") {
      setFormData((prev) => ({
        ...prev,
        assetTypeId: "",
        assetTypeName: ""
      }));
    }
  };

  const [updateProjectGroup, { loading: mutationLoading }] = useMutation(UPDATE_PROJECT_GROUP_MUTATION);

  const validateForm = () => {
    const errors = {};
    const requiredFields = ["name", "segment", "assetTypeId", "country"];

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        errors[field] = "This field is required";
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      // convert publicId to number or use 0, thus following the pattern in AddProjectGroupPage
      const publicIdValue = formData.publicId ? parseInt(formData.publicId, 10) : 0;

      const { data } = await updateProjectGroup({
        variables: {
          projectGroupId: projectGroup.id,
          projectGroup: {
            name: formData.name,
            groupAssetTypeId: formData.assetTypeId,
            country: formData.country,
            latitude: formData.latitude,
            longitude: formData.longitude,
            publicId: publicIdValue,
            region: formData.region,
            operator: formData.operator
          }
        }
      });

      if (data?.updateProjectGroup) {
        handleClose();
        setTimeout(() => {
          toast.success("Project group updated successfully.");
          navigate("/projects");
          handleEnvChange(env, tenantId, tenantName);
        }, 200);
      }
    } catch (error) {
      handleClose();
      setTimeout(() => {
        toast.error("Failed to update project group.");
        console.error("Update error:", error);
      }, 200);
    }
  };

  const countryOptions = Array.isArray(countries)
    ? countries.map((country) => {
        if (typeof country === "string") {
          return { value: country, label: country };
        }
        return {
          value: country.id || country.code || country.name,
          label: country.name
        };
      })
    : [];

  const [filteredAssetTypes, setFilteredAssetTypes] = useState(assetTypes || []);

  // filter asset types based on segment
  useEffect(() => {
    if (formData.segment && assetTypes) {
      const filtered = assetTypes.filter((type) => type.segment === formData.segment);
      setFilteredAssetTypes(filtered);
    } else {
      setFilteredAssetTypes(assetTypes || []);
    }
  }, [formData.segment, assetTypes]);

  const assetTypeOptions = filteredAssetTypes?.map((assetType) => ({
    value: assetType.id,
    label: assetType.name
  }));

  const segmentOptions =
    segments && segments.length > 0
      ? segments.map((segment) => {
          if (typeof segment === "string") {
            return { value: segment, label: segment };
          }
          return {
            value: segment.id || segment.value || segment.name,
            label: segment.name
          };
        })
      : Array.from(new Set(assetTypes.map((type) => type.segment)))
          .filter(Boolean)
          .map((segment) => ({ value: segment, label: segment }));

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="d-flex justify-content-center">
        <Modal.Title className="w-100 text-center">{viewOnly ? "View Project Group" : "Edit Project Group"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              {/* Name */}
              <Form.Group className="mb-3" controlId="formName">
                <Form.Label className="font-weight-bold">
                  <strong>Name</strong>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={viewOnly}
                  isInvalid={!!formErrors.name}
                />
                {formErrors.name && <Form.Control.Feedback type="invalid">{formErrors.name}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={6}>
              {/* Operator */}
              <Form.Group className="mb-3" controlId="formOperator">
                <Form.Label className="font-weight-bold">
                  <strong>Operator</strong>
                </Form.Label>
                <Form.Control type="text" name="operator" value={formData.operator} onChange={handleInputChange} disabled={viewOnly} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              {/* Country */}
              <Form.Group className="mb-3" controlId="formCountry">
                <Form.Label className="font-weight-bold">
                  <strong>Country</strong>
                </Form.Label>
                <Select
                  name="country"
                  options={countryOptions}
                  value={(() => {
                    if (!formData.country) return null;

                    // if country is in the list as a string, create a matching option
                    if (typeof formData.country === "string") {
                      return { value: formData.country, label: formData.country };
                    }

                    return null;
                  })()}
                  onChange={(selected) => handleSelectChange(selected, { name: "country" })}
                  isDisabled={viewOnly}
                  placeholder="Select country"
                  isClearable
                  isSearchable
                  className={formErrors.country ? "is-invalid" : ""}
                />
                {formErrors.country && <div className="invalid-feedback d-block">{formErrors.country}</div>}
              </Form.Group>
            </Col>
            <Col md={6}>
              {/* Region */}
              <Form.Group className="mb-3" controlId="formRegion">
                <Form.Label className="font-weight-bold">
                  <strong>Region</strong>
                </Form.Label>
                <Form.Control type="text" name="region" value={formData.region} onChange={handleInputChange} disabled={viewOnly} />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              {/* Segment */}
              <Form.Group className="mb-3" controlId="formSegment">
                <Form.Label className="font-weight-bold">
                  <strong>Segment</strong>
                </Form.Label>
                <Select
                  name="segment"
                  options={segmentOptions}
                  value={(() => {
                    if (!formData.segment) return null;
                    if (typeof formData.segment === "string") {
                      return { value: formData.segment, label: formData.segment };
                    }

                    return null;
                  })()}
                  onChange={(selected) => handleSelectChange(selected, { name: "segment" })}
                  isDisabled={viewOnly}
                  placeholder="Select Segment"
                  isClearable
                  isSearchable
                  className={formErrors.segment ? "is-invalid" : ""}
                />
                {formErrors.segment && <div className="invalid-feedback d-block">{formErrors.segment}</div>}
              </Form.Group>
            </Col>
            <Col md={6}>
              {/* Asset Type */}
              <Form.Group className="mb-3" controlId="formAssetType">
                <Form.Label className="font-weight-bold">
                  <strong>Asset Type</strong>
                </Form.Label>
                <Select
                  name="assetTypeId"
                  options={assetTypeOptions}
                  value={(() => {
                    if (!formData.assetTypeName) return null;
                    if (typeof formData.assetTypeName === "string") {
                      return { value: formData.assetTypeId, label: formData.assetTypeName };
                    }
                    return null;
                  })()}
                  onChange={(selected) => handleSelectChange(selected, { name: "assetTypeId" })}
                  isDisabled={viewOnly || !formData.segment}
                  placeholder={formData.segment ? "Select Asset Type" : "Select a Segment first"}
                  isClearable={false}
                  isSearchable
                  className={formErrors.assetTypeId ? "is-invalid" : ""}
                />
                {formErrors.assetTypeId && <div className="invalid-feedback d-block">{formErrors.assetTypeId}</div>}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              {/* Public ID */}
              <Form.Group className="mb-3" controlId="formPublicId">
                <Form.Label className="font-weight-bold">
                  <strong>Public ID</strong>
                </Form.Label>
                <Form.Control type="number" name="publicId" value={formData.publicId} onChange={handleInputChange} disabled={viewOnly} />
              </Form.Group>
            </Col>
            <Col md={4}>
              {/* Latitude */}
              <Form.Group className="mb-3" controlId="formLatitude">
                <Form.Label className="font-weight-bold">
                  <strong>Latitude</strong>
                </Form.Label>
                <Form.Control type="text" name="latitude" value={formData.latitude} onChange={handleInputChange} disabled={viewOnly} />
              </Form.Group>
            </Col>
            <Col md={4}>
              {/* Longitude */}
              <Form.Group className="mb-3" controlId="formLongitude">
                <Form.Label className="font-weight-bold">
                  <strong>Longitude</strong>
                </Form.Label>
                <Form.Control type="text" name="longitude" value={formData.longitude} onChange={handleInputChange} disabled={viewOnly} />
              </Form.Group>
            </Col>
          </Row>

          {!viewOnly && (
            <div className="d-grid mt-4">
              <Button variant="primary" type="submit" style={{ width: "50%" }} className="mx-auto" disabled={mutationLoading}>
                {mutationLoading ? "Updating project group..." : "Update Project Group"}
              </Button>
            </div>
          )}
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export { EditProjectGroupModal };
